import { type JSX, Fragment, useCallback } from 'react';

import { CodeSpan } from '../CodeSpan';
import type { MeasureType, Metric } from '../types';

import styles from './RandomizationUnitsSection.module.css';

interface RandomizationUnitsSectionProps {
  measureType: MeasureType;
  metricKind: Metric['kind'];
  unitAggregationType: Metric['unitAggregationType'];
  formElement?: JSX.Element;
  readonly?: boolean;
  readonlyValues?: string[];
  /** When true, omits the trailing ", where" connector intended to precede success criteria. Defaults to false. */
  omitWhereConnector?: boolean;
}

const RandomizationUnits = ({
  readonlyValues,
}: {
  readonlyValues: NonNullable<RandomizationUnitsSectionProps['readonlyValues']>;
}) => {
  const findPrefix = useCallback(
    (index: number) => {
      if (readonlyValues.length > 2) {
        // use the formatting: "X, Y, and Z"
        return index === 0 ? (
          ''
        ) : index === readonlyValues.length - 1 ? (
          <span className={styles.orText}>or</span>
        ) : (
          ', '
        );
      } else if (readonlyValues.length === 2) {
        // use the formatting: "X or Y"
        return index === readonlyValues.length - 1 && <span className={styles.orText}>or</span>;
      }
    },
    [readonlyValues],
  );

  if (readonlyValues.length === 0) {
    return null;
  }

  if (readonlyValues.length >= 2) {
    return (
      <>
        {readonlyValues.map((rov, index) => {
          const isOrCase = index > 0 && index === readonlyValues.length - 1;
          return (
            <Fragment key={rov}>
              {isOrCase && ' '}
              <span className={styles.commaSpan}>
                {findPrefix(index)}
                <CodeSpan textValue={rov} />
              </span>
            </Fragment>
          );
        })}
      </>
    );
  }

  // use the formatting: "X"
  return <CodeSpan textValue={readonlyValues[0]} />;
};

export const RandomizationUnitsSection: React.FC<RandomizationUnitsSectionProps> = ({
  measureType,
  metricKind,
  unitAggregationType,
  readonlyValues,
  formElement,
  readonly = false,
  omitWhereConnector = false,
}: RandomizationUnitsSectionProps) => (
  <>
    {getFirstCopySection(measureType, metricKind)}
    {readonly && Array.isArray(readonlyValues) ? <RandomizationUnits readonlyValues={readonlyValues} /> : formElement}
    {getSecondCopySection(measureType, metricKind, unitAggregationType, omitWhereConnector)}
  </>
);
const getFirstCopySection = (selectedMeasureType: MeasureType, metricKind: Metric['kind']) => {
  if (selectedMeasureType === 'occurrence') {
    return <span>Percentage of </span>;
  }
  switch (metricKind) {
    case 'custom':
    case 'trace':
      if (selectedMeasureType === 'value') {
        return (
          <span>
            <CodeSpan textValue="value" /> of the per{' '}
          </span>
        );
      } else if (selectedMeasureType === 'count') {
        const measureUnit = metricKind === 'trace' ? 'span' : 'event';
        return (
          <span>
            of {measureUnit} <CodeSpan textValue="count" /> per{' '}
          </span>
        );
      }
      break;
    case 'click':
      return 'clicks per';
    case 'pageview':
      return 'page views per';
    default:
      return null;
  }
};

const getSecondCopySection = (
  selectedMeasureType: MeasureType,
  metricKind: Metric['kind'],
  unitAggregationType: Metric['unitAggregationType'],
  omitWhereConnector: boolean,
) => {
  if (selectedMeasureType === 'count') {
    return omitWhereConnector ? null : ' where ';
  }
  switch (metricKind) {
    case 'custom':
    case 'trace':
      if (selectedMeasureType === 'value') {
        const measureUnit = metricKind === 'trace' ? 'span' : 'event';
        return (
          <span>
            {' '}
            {measureUnit}{' '}
            <span className={styles.commaSpan}>
              <CodeSpan textValue={unitAggregationType?.toString() ?? ''} />
              {omitWhereConnector ? null : ','}
            </span>
            {omitWhereConnector ? null : ' where '}
          </span>
        );
      } else if (selectedMeasureType === 'occurrence') {
        const measureUnit = metricKind === 'trace' ? 'a matching span' : 'the event';
        return omitWhereConnector ? ` units that sent ${measureUnit}` : ` units that sent ${measureUnit}, where `;
      }
      break;
    case 'click':
      return omitWhereConnector ? ' units that clicked' : ' units that clicked, where ';
    case 'pageview':
      return omitWhereConnector ? ' units that saw the page' : ' units that saw the page, where ';
    default:
      break;
  }
};
